import React, { useState } from "react";
import { patientLabel, validateNewPatient } from "../model/cnoRules";
import {
    backdropStyle,
    closeButtonStyle,
    contextMenuDangerItemStyle,
    contextMenuStyle,
    dangerButtonStyle,
    errorStyle,
    inputStyle,
    labelStyle,
    modalActionsStyle,
    modalHeaderStyle,
    modalStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "./cnoStyles";

const modalTitleStyle = { margin: 0, fontSize: "18px", color: "#111827" };
const confirmTextStyle = { margin: "0 0 18px", color: "#5A6D68", lineHeight: 1.5 };
const confirmNameStyle = { color: "#0C3B3C" };

// Fond, boîte de dialogue et en-tête communs aux modales CNO.
export function CnoModalFrame({ label, title, onClose, closeDisabled, style = modalStyle, children }) {
    return (
        <div style={backdropStyle} role="presentation" onMouseDown={closeDisabled ? undefined : onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={label}
                onMouseDown={event => event.stopPropagation()}
                style={style}
            >
                <div style={modalHeaderStyle}>
                    <h2 style={modalTitleStyle}>{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        onMouseDown={onClose}
                        disabled={closeDisabled}
                        aria-label="Fermer la fenêtre modale"
                        style={closeButtonStyle}
                    >×</button>
                </div>
                {children}
            </div>
        </div>
    );
}

const PATIENT_FIELDS = [
    ["nom", "Nom"],
    ["prenom", "Prénom"],
    ["adresse", "Adresse"],
    ["telephone", "Numéro de téléphone"],
];

export function AddPatientModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ nom: "", prenom: "", adresse: "", telephone: "" });
    const [error, setError] = useState("");

    const submit = event => {
        event.preventDefault();
        const result = validateNewPatient(form);
        if (result.error) {
            setError(result.error);
            return;
        }
        onCreated(result.fields);
    };

    return (
        <CnoModalFrame label="Ajouter un patient" title="Ajouter un patient" onClose={onClose}>
            <form onSubmit={submit} style={{ display: "grid", gap: "14px" }}>
                {error && <p role="alert" style={errorStyle}>{error}</p>}
                {PATIENT_FIELDS.map(([field, label], index) => (
                    <label key={field} style={labelStyle}>
                        {label}
                        <input
                            autoFocus={index === 0}
                            style={inputStyle}
                            value={form[field]}
                            onChange={event => setForm(current => ({ ...current, [field]: event.target.value }))}
                        />
                    </label>
                ))}
                <div style={modalActionsStyle}>
                    <button type="button" onClick={onClose} style={secondaryButtonStyle}>Annuler</button>
                    <button type="submit" style={primaryButtonStyle}>Ajouter le patient</button>
                </div>
            </form>
        </CnoModalFrame>
    );
}

function ConfirmDeleteModal({ label, title, children, confirmLabel, onClose, onConfirm, deleting }) {
    return (
        <CnoModalFrame label={label} title={title} onClose={onClose}>
            <p style={confirmTextStyle}>{children}</p>
            <div style={modalActionsStyle}>
                <button type="button" onClick={onClose} disabled={deleting} style={secondaryButtonStyle}>Annuler</button>
                <button type="button" onClick={onConfirm} disabled={deleting} style={dangerButtonStyle}>
                    {deleting ? "Suppression..." : confirmLabel}
                </button>
            </div>
        </CnoModalFrame>
    );
}

export function DeleteProductModal({ line, onClose, onConfirm, deleting }) {
    return (
        <ConfirmDeleteModal
            label="Confirmer la suppression du produit"
            title="Supprimer ce produit ?"
            confirmLabel="Supprimer"
            onClose={onClose}
            onConfirm={onConfirm}
            deleting={deleting}
        >
            Le produit <strong style={confirmNameStyle}>{line?.produit || "sans nom"}</strong> sera supprimé de la fiche CNO.
        </ConfirmDeleteModal>
    );
}

export function DeletePatientModal({ patient, onClose, onConfirm, deleting }) {
    return (
        <ConfirmDeleteModal
            label="Confirmer la suppression du patient"
            title="Supprimer ce patient ?"
            confirmLabel="Supprimer le patient"
            onClose={onClose}
            onConfirm={onConfirm}
            deleting={deleting}
        >
            La fiche de <strong style={confirmNameStyle}>{patientLabel(patient)}</strong> et ses lignes CNO seront supprimées.
        </ConfirmDeleteModal>
    );
}

export function PatientContextMenu({ menu, onDelete, onClose }) {
    if (!menu) return null;
    return (
        <div
            role="menu"
            tabIndex={-1}
            aria-label={`Actions patient ${patientLabel(menu.patient)}`}
            style={contextMenuStyle(menu.x, menu.y)}
            onMouseDown={event => event.stopPropagation()}
        >
            <button
                type="button"
                role="menuitem"
                onClick={() => {
                    onDelete(menu.patient);
                    onClose();
                }}
                style={contextMenuDangerItemStyle}
            >
                Supprimer le patient
            </button>
        </div>
    );
}
